import React, { useRef } from 'react';
import { useResponsiveCanvas } from './prologue/useResponsiveCanvas';
import { PrologueAssembly } from './prologue/PrologueAssembly';
import { PrologueCataclysm } from './prologue/PrologueCataclysm';
import { PrologueInvestiture } from './prologue/PrologueInvestiture';
import { PrologueDeparture } from './prologue/PrologueDeparture';

interface IntroCutsceneArtworkProps {
  introStep: number;
}

interface SceneMetadata {
  numeral: string;
  tifinagh: string;
  title: string;
  ambientBg: string;
}

const SCENE_INFO: Record<number, SceneMetadata> = {
  0: {
    numeral: 'ACTE I',
    tifinagh: 'ⵜⴰⵊⵎⴰⵄⵜ',
    title: "L'Assemblée de la Tajmaɛt",
    ambientBg: 'bg-[#030712]',
  },
  1: {
    numeral: 'ACTE I',
    tifinagh: 'ⵜⴰⵊⵎⴰⵄⵜ',
    title: "L'Assemblée de la Tajmaɛt",
    ambientBg: 'bg-[#030712]',
  },
  2: {
    numeral: 'ACTE II',
    tifinagh: 'ⴰⵖⵛⵛⵓ',
    title: 'Le Cataclysme de Lǧemɛa n Tizi',
    ambientBg: 'bg-[#02120e]',
  },
  3: {
    numeral: 'ACTE II',
    tifinagh: 'ⴰⵖⵛⵛⵓ',
    title: 'Le Brouillard d’Oubli',
    ambientBg: 'bg-[#02120e]',
  },
  4: {
    numeral: 'ACTE III',
    tifinagh: 'ⵜⴰⴱⵣⵉⵎⵜ',
    title: "L'Investiture du Yaz",
    ambientBg: 'bg-[#030814]',
  },
  5: {
    numeral: 'ACTE III',
    tifinagh: 'ⵜⴰⴱⵣⵉⵎⵜ',
    title: "L'Amulette Ancestrale",
    ambientBg: 'bg-[#030814]',
  },
  6: {
    numeral: 'ACTE IV',
    tifinagh: 'ⵜⴰⴼⴰⵜ',
    title: "L'Aube sur le Djurdjura",
    ambientBg: 'bg-[#1e1b4b]',
  },
  7: {
    numeral: 'ACTE IV',
    tifinagh: 'ⵜⴰⴼⴰⵜ',
    title: 'Le Départ du Héros',
    ambientBg: 'bg-[#1e1b4b]',
  },
};

export const IntroCutsceneArtwork: React.FC<IntroCutsceneArtworkProps> = React.memo(({ introStep }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dimensions = useResponsiveCanvas(containerRef);

  const sceneInfo = SCENE_INFO[introStep] || SCENE_INFO[0];

  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative overflow-hidden flex items-center justify-center select-none transition-colors duration-1000 ${sceneInfo.ambientBg}`}
    >
      {/* 1. SCENE 1: Tajmaɛt at Night (Steps 0, 1) */}
      {(introStep === 0 || introStep === 1) && (
        <div className="absolute inset-0 w-full h-full animate-[fadeIn_0.6s_ease-out]">
          <PrologueAssembly dimensions={dimensions} />
        </div>
      )}

      {/* 2. SCENE 2: The Cataclysm on Lǧemɛa n Tizi & The Dissolving Words (Steps 2, 3) */}
      {(introStep === 2 || introStep === 3) && (
        <div className="absolute inset-0 w-full h-full animate-[fadeIn_0.6s_ease-out]">
          <PrologueCataclysm dimensions={dimensions} />
        </div>
      )}

      {/* 3. SCENE 3: The Sacred Investiture of the Yaz Talisman (Steps 4, 5) */}
      {(introStep === 4 || introStep === 5) && (
        <div className="absolute inset-0 w-full h-full animate-[fadeIn_0.6s_ease-out]">
          <PrologueInvestiture dimensions={dimensions} />
        </div>
      )}

      {/* 4. SCENE 4: Hero's Departure at Dawn on the Djurdjura Highlands (Steps 6, 7) */}
      {(introStep === 6 || introStep === 7) && (
        <div className="absolute inset-0 w-full h-full animate-[fadeIn_0.6s_ease-out]">
          <PrologueDeparture dimensions={dimensions} />
        </div>
      )}

      {/* Subtle Cinematic Vignette along bottom border to seamlessly merge into the dialogue panel */}
      <div className="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none z-10" />
    </div>
  );
});
